import React, { useState, useEffect, useContext, useRef, useMemo } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { alpha, makeStyles } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { FormControl, FormControlLabel, Switch, Chip, Menu, MenuItem } from "@material-ui/core";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import moment from "moment";
import { AuthContext } from "../../context/Auth/AuthContext";
import { isArray } from "lodash";
import DeleteOutline from "@material-ui/icons/DeleteOutline";
import AttachFile from "@material-ui/icons/AttachFile";
import { head } from "lodash";
import ConfirmationModal from "../ConfirmationModal";
import MessageVariablesPicker from "../MessageVariablesPicker";
import { FormDialogHeader, FormSection, useFormDialogStyles } from "../FormDialog";
import { relativeTime, scheduleState, attentionReason } from "../../helpers/scheduleStatus";
import { previewBody } from "../../helpers/scheduleBodyPreview";
import formatPhone from "../../helpers/formatPhone";
import LogoLoader from "../LogoLoader";
import useQuickMessages from "../../hooks/useQuickMessages";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    paper: { borderRadius: 16 },
    layout: {
      display: "flex",
      alignItems: "stretch",
      padding: 0,
      overflow: "hidden",
    },
    form: { flex: 1, minWidth: 0, padding: theme.spacing(3), overflowY: "auto" },
    preview: {
      width: 300,
      flexShrink: 0,
      padding: theme.spacing(3, 2.5),
      backgroundColor: dark ? "#1b2328" : "#EFEAE2",
      borderLeft: `1px solid ${theme.palette.divider}`,
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
      [theme.breakpoints.down("sm")]: { display: "none" },
    },
    previewTitle: {
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
    },
    bubble: {
      alignSelf: "flex-end",
      maxWidth: "100%",
      padding: theme.spacing(0.75),
      borderRadius: "10px 2px 10px 10px",
      backgroundColor: dark ? "#005C4B" : "#D9FDD3",
      color: dark ? "#E9EDEF" : "#111B21",
      boxShadow: "0 1px 1px rgba(0,0,0,0.12)",
    },
    bubbleText: {
      fontSize: 14,
      lineHeight: "20px",
      padding: theme.spacing(0, 0.5),
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    bubbleTime: { textAlign: "right", fontSize: 11, opacity: 0.7, padding: theme.spacing(0.25, 0.5, 0) },
    bubbleFile: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      padding: theme.spacing(1),
      marginBottom: theme.spacing(0.75),
      borderRadius: 7,
      backgroundColor: "rgba(0,0,0,0.07)",
      fontSize: 13,
      fontWeight: 600,
      wordBreak: "break-all",
    },
    previewHint: { fontSize: 12, color: theme.palette.text.secondary },
    contactOption: { display: "flex", flexDirection: "column" },
    contactPhone: { fontSize: 12, color: theme.palette.text.secondary },
    quickRow: { display: "flex", gap: theme.spacing(0.75), flexWrap: "wrap" },
    dateRow: { display: "flex", gap: theme.spacing(1.25) },
    confirmLine: { fontSize: 13, color: theme.palette.text.secondary },
    messageBox: {
      "& textarea": {
        scrollbarWidth: "thin",
        scrollbarColor: `${dark ? "#555" : "#bdbdbd"} transparent`,
      },
      "& .MuiOutlinedInput-root": { borderRadius: 10 },
    },
    fieldFooter: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexWrap: "wrap",
    },
    switchHelp: { fontSize: 13, color: theme.palette.text.secondary, marginLeft: 48, marginTop: -4 },
    banner: {
      display: "flex",
      alignItems: "flex-start",
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 3),
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    bannerInfo: {
      backgroundColor: dark ? "#0d2a4a" : "#e3f2fd",
      color: dark ? "#90caf9" : "#1565c0",
    },
    bannerError: {
      backgroundColor: dark ? "#3e1010" : "#ffebee",
      color: dark ? "#ef9a9a" : "#c62828",
    },
    deleteButton: {
      marginRight: "auto",
      color: theme.palette.error.main,
    },
    chipAttach: {
      backgroundColor: alpha(theme.palette.primary.main, dark ? 0.2 : 0.1),
      color: dark ? "#CFE0FA" : theme.palette.primary.dark,
      fontWeight: 600,
    },
  };
});

const filterOptions = createFilterOptions({ limit: 25 });

const SEND_FORMAT = "YYYY-MM-DDTHH:mm";

// Atalhos de horário. Só aparecem os que ainda estão no futuro.
const buildQuickOptions = () => {
  const now = moment();
  const options = [{ label: "Em 1 hora", value: now.clone().add(1, "hour").format(SEND_FORMAT) }];
  const today18 = now.clone().hour(18).minute(0).second(0);
  if (today18.isAfter(now.clone().add(30, "minutes"))) {
    options.push({ label: "Hoje 18:00", value: today18.format(SEND_FORMAT) });
  }
  options.push({
    label: "Amanhã 09:00",
    value: now.clone().add(1, "day").hour(9).minute(0).format(SEND_FORMAT),
  });
  const monday = now.clone().add(1, "week").isoWeekday(1).hour(9).minute(0);
  if (!monday.isSame(now.clone().add(1, "day"), "day")) {
    options.push({ label: "Segunda 09:00", value: monday.format(SEND_FORMAT) });
  }
  return options;
};

const describeSendAt = (sendAt) => {
  const m = moment(sendAt, SEND_FORMAT);
  if (!m.isValid()) return "";
  const time = m.format("HH:mm");
  if (m.isSame(moment(), "day")) return `hoje ${time}`;
  if (m.isSame(moment().add(1, "day"), "day")) return `amanhã ${time}`;
  return `${m.format("DD/MM")} ${time}`;
};

const WEEKDAYS = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];

const ScheduleModal = ({
  open,
  onClose,
  scheduleId,
  contactId,
  cleanContact,
  reload,
  selectedDate,
  duplicateFrom,
}) => {
  const classes = useStyles();
  const dialogClasses = useFormDialogStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  const defaultSendAt = () =>
    selectedDate
      ? moment(selectedDate).format(SEND_FORMAT)
      : moment().add(1, "hour").format(SEND_FORMAT);

  const emptySchedule = () => ({
    body: "",
    contactId: "",
    sendAt: defaultSendAt(),
    sentAt: "",
    status: "",
  });

  const [schedule, setSchedule] = useState(emptySchedule());
  const [currentContact, setCurrentContact] = useState({ id: "", name: "" });
  const [contacts, setContacts] = useState([{ id: "", name: "" }]);
  const [queues, setQueues] = useState([]);
  const [keepTicketOpen, setKeepTicketOpen] = useState(true);
  const [scheduleQueueId, setScheduleQueueId] = useState("");
  const [attachment, setAttachment] = useState(null);
  // Mídia já salva removida neste modal. Fica fora de `schedule` porque alterar
  // `schedule` reinicializa o Formik e apagaria o que o usuário já editou.
  const [mediaDeleted, setMediaDeleted] = useState(false);
  const attachmentFile = useRef(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const messageInputRef = useRef();
  const [loadingData, setLoadingData] = useState(false);
  // A mensagem aberta não saiu (erro ou horário vencido). Fica em estado porque
  // o horário é trocado ao abrir e a regra deixaria de valer.
  const [needsAttention, setNeedsAttention] = useState(false);
  // Duplicando: id da mensagem de origem, para copiar o anexo ao salvar
  const [copyMediaFromId, setCopyMediaFromId] = useState(null);
  const [quickMessages, setQuickMessages] = useState([]);
  const [quickAnchor, setQuickAnchor] = useState(null);
  const quickApi = useQuickMessages();
  const isDuplicate = !scheduleId && !!duplicateFrom;

  const isReadOnly = !!schedule.sentAt && schedule.sentAt !== "";
  const savedMediaPath = mediaDeleted ? null : schedule.mediaPath;

  // Horário no passado só é aceito em mensagens que não vão mais ser editadas
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        body: Yup.string().min(5, "Mensagem muito curta").required("Obrigatório"),
        contactId: Yup.number().required("Escolha um contato"),
        sendAt: Yup.string()
          .required("Obrigatório")
          .test("future", "Escolha um horário no futuro", (value) =>
            isReadOnly ? true : moment(value, SEND_FORMAT).isAfter(moment())
          ),
      }),
    [isReadOnly]
  );

  const getDialogTitle = () => {
    if (isDuplicate) return "Duplicar mensagem";
    if (!scheduleId) return "Nova mensagem agendada";
    if (needsAttention) return "Reagendar mensagem";
    if (isReadOnly) return "Mensagem enviada";
    return "Editar mensagem agendada";
  };

  const getSubtitle = () => {
    if (isReadOnly) return "Esta mensagem já foi enviada e não pode ser alterada.";
    return "A mensagem sai pelo WhatsApp da empresa no horário escolhido.";
  };

  useEffect(() => {
    if (!open) return;

    const initialSendAt = defaultSendAt();

    setSchedule({ ...emptySchedule(), sendAt: initialSendAt });
    setCurrentContact({ id: "", name: "" });
    setKeepTicketOpen(true);
    setScheduleQueueId("");
    setMediaDeleted(false);
    setNeedsAttention(false);
    setCopyMediaFromId(null);

    (async () => {
      setLoadingData(true);
      try {
        const [{ data: contactList }, { data: queueList }] = await Promise.all([
          api.get("/contacts/list", { params: { companyId: user.companyId } }),
          api.get("/queue"),
        ]);
        const mapped = isArray(contactList)
          ? contactList.map((c) => ({ id: c.id, name: c.name, number: c.number }))
          : [];
        setContacts([{ id: "", name: "" }, ...mapped]);
        setQueues(isArray(queueList) ? queueList : []);

        if (contactId) {
          setSchedule((prev) => ({ ...prev, contactId, sendAt: initialSendAt }));
        }

        if (!scheduleId && duplicateFrom) {
          // Duplicar: copia contato, texto, fila e anexo; o horário é novo
          const { data } = await api.get(`/schedules/${duplicateFrom.id}`);
          setSchedule((prev) => ({
            ...prev,
            body: data.body,
            contactId: data.contactId,
            mediaPath: data.mediaPath,
            mediaName: data.mediaName,
          }));
          setKeepTicketOpen(data.keepTicketOpen ?? true);
          setScheduleQueueId(data.scheduleQueueId ?? "");
          setCurrentContact(data.contact || { id: "", name: "" });
          if (data.mediaPath) setCopyMediaFromId(data.id);
          return;
        }

        if (!scheduleId) return;

        const { data } = await api.get(`/schedules/${scheduleId}`);
        // Mensagem que não saiu: já abre com um horário novo, no futuro
        const attention = scheduleState(data) === "attention";
        const sendAt = attention
          ? moment().add(1, "hour").format(SEND_FORMAT)
          : moment(data.sendAt).format(SEND_FORMAT);
        setNeedsAttention(attention);
        setSchedule((prev) => ({ ...prev, ...data, sendAt }));
        setKeepTicketOpen(data.keepTicketOpen ?? true);
        setScheduleQueueId(data.scheduleQueueId ?? "");
        setCurrentContact(data.contact || { id: "", name: "" });
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleId, contactId, open, user, selectedDate, duplicateFrom]);

  // Respostas rápidas para inserir no texto
  useEffect(() => {
    if (!open) return;
    quickApi
      .list()
      .then((list) => setQuickMessages(isArray(list) ? list : []))
      .catch(() => setQuickMessages([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (contactId && contacts.length > 1) {
      const contact = contacts.find((c) => c.id === contactId);
      if (contact) setCurrentContact(contact);
    }
  }, [contactId, contacts]);

  const handleDelete = async () => {
    try {
      await api.delete(`/schedules/${scheduleId}`);
      toast.success("Agendamento excluído com sucesso.");
      if (typeof reload === "function") reload();
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleClose = () => {
    onClose();
    setAttachment(null);
    setSchedule(emptySchedule());
    setCurrentContact({ id: "", name: "" });
    setQueues([]);
    setKeepTicketOpen(true);
    setScheduleQueueId("");
    setMediaDeleted(false);
    setCopyMediaFromId(null);
    setQuickAnchor(null);
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) setAttachment(file);
  };

  const handleSaveSchedule = async (values) => {
    const scheduleData = {
      ...values,
      userId: user.id,
      keepTicketOpen,
      scheduleQueueId: keepTicketOpen ? (scheduleQueueId || null) : null,
    };
    try {
      if (scheduleId) {
        await api.put(`/schedules/${scheduleId}`, scheduleData);
        if (attachment) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/schedules/${scheduleId}/media-upload`, formData);
        }
      } else {
        const { data } = await api.post("/schedules", scheduleData);
        if (attachment) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/schedules/${data.id}/media-upload`, formData);
        } else if (copyMediaFromId && !mediaDeleted) {
          await api.post(`/schedules/${data.id}/media-copy`, { sourceId: copyMediaFromId });
        }
      }
      toast.success(i18n.t("scheduleModal.success"));
      if (typeof reload === "function") reload();
      if (contactId && typeof cleanContact === "function") {
        cleanContact();
        history.push("/schedules");
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleClickMsgVar = async (msgVar, setValueFunc) => {
    const el = messageInputRef.current;
    const firstHalf = el.value.substring(0, el.selectionStart);
    const secondHalf = el.value.substring(el.selectionEnd);
    const newCursorPos = el.selectionStart + msgVar.length;
    setValueFunc("body", `${firstHalf}${msgVar}${secondHalf}`);
    await new Promise((r) => setTimeout(r, 100));
    messageInputRef.current.setSelectionRange(newCursorPos, newCursorPos);
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }
    if (savedMediaPath && copyMediaFromId) {
      // Anexo copiado da mensagem original: só deixa de copiar, sem apagar o dela
      setMediaDeleted(true);
      return;
    }
    if (savedMediaPath) {
      await api.delete(`/schedules/${schedule.id}/media-upload`);
      setMediaDeleted(true);
      toast.success(i18n.t("scheduleModal.toasts.deleted"));
      if (typeof reload === "function") reload();
    }
  };

  const attachmentName = attachment ? attachment.name : savedMediaPath ? schedule.mediaName : "";

  return (
    <div>
      <ConfirmationModal
        title={i18n.t("scheduleModal.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("scheduleModal.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <ConfirmationModal
        title="Excluir agendamento"
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        Tem certeza que deseja excluir este agendamento? Esta ação não pode ser desfeita.
      </ConfirmationModal>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        classes={{ paper: classes.paper }}
        aria-labelledby="schedule-modal-title"
      >
        {loadingData && <LogoLoader overlay />}
        <FormDialogHeader
          id="schedule-modal-title"
          title={getDialogTitle()}
          subtitle={getSubtitle()}
          onClose={handleClose}
        />

        {isReadOnly && (
          <Box className={`${classes.banner} ${classes.bannerInfo}`}>
            <InfoOutlinedIcon fontSize="small" />
            <Typography variant="body2">
              Enviada em {moment(schedule.sentAt).format("DD/MM/YYYY [às] HH:mm")}.
            </Typography>
          </Box>
        )}

        {needsAttention && (
          <Box className={`${classes.banner} ${classes.bannerError}`}>
            <ErrorOutlineIcon fontSize="small" />
            <Typography variant="body2">
              {attentionReason(schedule)} Escolha um novo horário e salve para enviar de novo.
            </Typography>
          </Box>
        )}

        <div style={{ display: "none" }}>
          <input
            type="file"
            accept=".png,.jpg,.jpeg,.pdf,.mp3,.mp4,.ogg,.opus,.webp"
            ref={attachmentFile}
            onChange={handleAttachmentFile}
          />
        </div>

        <Formik
          initialValues={schedule}
          enableReinitialize={true}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            // Aguarda a requisição terminar: enquanto isso o Formik mantém
            // isSubmitting=true e o botão desabilitado, evitando envio duplicado.
            await handleSaveSchedule(values);
          }}
        >
          {({ touched, errors, isSubmitting, setFieldValue, values }) => {
            const sendMoment = moment(values.sendAt, SEND_FORMAT);
            const sendValid = sendMoment.isValid();
            const datePart = sendValid ? sendMoment.format("YYYY-MM-DD") : "";
            const timePart = sendValid ? sendMoment.format("HH:mm") : "";
            const setDatePart = (date) =>
              date && setFieldValue("sendAt", `${date}T${timePart || "09:00"}`);
            const setTimePart = (time) =>
              time && setFieldValue("sendAt", `${datePart || moment().format("YYYY-MM-DD")}T${time}`);
            const quickOptions = buildQuickOptions();
            const preview = previewBody(values.body, currentContact, sendValid ? sendMoment.toDate() : null);

            return (
              <Form style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
                <DialogContent dividers className={classes.layout}>
                  <div className={`${dialogClasses.content} ${classes.form}`}>
                    <FormSection title="Para">
                      <Autocomplete
                        fullWidth
                        disabled={isReadOnly}
                        value={currentContact}
                        options={contacts}
                        filterOptions={filterOptions}
                        onChange={(e, contact) => {
                          const id = contact ? contact.id : "";
                          // Não atualizar `schedule` aqui: com enableReinitialize o
                          // Formik seria reinicializado e apagaria o que já foi digitado.
                          setCurrentContact(contact || { id: "", name: "" });
                          setFieldValue("contactId", id);
                        }}
                        getOptionLabel={(option) => option.name || ""}
                        getOptionSelected={(option, value) => option.id === value.id}
                        renderOption={(option) => (
                          <div className={classes.contactOption}>
                            <span>{option.name}</span>
                            {option.number && (
                              <span className={classes.contactPhone}>{formatPhone(option.number)}</span>
                            )}
                          </div>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            size="small"
                            placeholder="Buscar contato por nome"
                            inputProps={{ ...params.inputProps, "aria-label": i18n.t("scheduleModal.form.contact") }}
                            error={touched.contactId && Boolean(errors.contactId)}
                            helperText={touched.contactId && errors.contactId}
                          />
                        )}
                      />
                    </FormSection>

                    <FormSection title="Mensagem">
                      <Field
                        as={TextField}
                        className={classes.messageBox}
                        rows={5}
                        multiline
                        placeholder="Escreva a mensagem que será enviada"
                        inputProps={{ "aria-label": i18n.t("scheduleModal.form.body") }}
                        name="body"
                        inputRef={messageInputRef}
                        error={touched.body && Boolean(errors.body)}
                        helperText={touched.body && errors.body}
                        variant="outlined"
                        fullWidth
                        disabled={isReadOnly}
                      />
                      {!isReadOnly && (
                        <MessageVariablesPicker
                          variant="inline"
                          disabled={isSubmitting}
                          onClick={(value) => handleClickMsgVar(value, setFieldValue)}
                        />
                      )}
                      <div className={classes.fieldFooter}>
                        {attachmentName ? (
                          <Chip
                            className={classes.chipAttach}
                            icon={<AttachFile />}
                            label={attachmentName}
                            onDelete={isReadOnly ? undefined : () => setConfirmationOpen(true)}
                            deleteIcon={<DeleteOutline />}
                          />
                        ) : (
                          !isReadOnly && (
                            <Button
                              size="small"
                              color="primary"
                              startIcon={<AttachFile />}
                              onClick={() => attachmentFile.current.click()}
                              disabled={isSubmitting}
                              className={dialogClasses.textButton}
                            >
                              Anexar arquivo
                            </Button>
                          )
                        )}
                        {!isReadOnly && quickMessages.length > 0 && (
                          <Button
                            size="small"
                            color="primary"
                            startIcon={<FlashOnIcon />}
                            onClick={(e) => setQuickAnchor(e.currentTarget)}
                            disabled={isSubmitting}
                            className={dialogClasses.textButton}
                          >
                            Resposta rápida
                          </Button>
                        )}
                        <Menu
                          anchorEl={quickAnchor}
                          open={Boolean(quickAnchor)}
                          onClose={() => setQuickAnchor(null)}
                          getContentAnchorEl={null}
                          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                          PaperProps={{ style: { maxHeight: 320, maxWidth: 420 } }}
                        >
                          {quickMessages.map((q) => (
                            <MenuItem
                              key={q.id}
                              onClick={() => {
                                setQuickAnchor(null);
                                handleClickMsgVar(q.message, setFieldValue);
                              }}
                            >
                              <div className={classes.contactOption}>
                                <strong>/{q.shortcode}</strong>
                                <span className={classes.contactPhone}>
                                  {(q.message || "").slice(0, 90)}
                                  {(q.message || "").length > 90 ? "…" : ""}
                                </span>
                              </div>
                            </MenuItem>
                          ))}
                        </Menu>
                        <span style={{ flex: 1 }} />
                        <Typography variant="caption" color="textSecondary">
                          {(values.body || "").length} caracteres
                        </Typography>
                      </div>
                    </FormSection>

                    <FormSection title="Quando enviar">
                      {!isReadOnly && (
                        <div className={classes.quickRow}>
                          {quickOptions.map((option) => (
                            <Chip
                              key={option.label}
                              label={option.label}
                              clickable
                              size="small"
                              color={values.sendAt === option.value ? "primary" : "default"}
                              variant={values.sendAt === option.value ? "default" : "outlined"}
                              onClick={() => setFieldValue("sendAt", option.value)}
                            />
                          ))}
                        </div>
                      )}
                      <div className={classes.dateRow}>
                        <TextField
                          label="Data"
                          type="date"
                          variant="outlined"
                          size="small"
                          style={{ flex: 1 }}
                          InputLabelProps={{ shrink: true }}
                          value={datePart}
                          onChange={(e) => setDatePart(e.target.value)}
                          disabled={isReadOnly}
                          error={touched.sendAt && Boolean(errors.sendAt)}
                        />
                        <TextField
                          label="Horário"
                          type="time"
                          variant="outlined"
                          size="small"
                          style={{ width: 140 }}
                          InputLabelProps={{ shrink: true }}
                          value={timePart}
                          onChange={(e) => setTimePart(e.target.value)}
                          disabled={isReadOnly}
                          error={touched.sendAt && Boolean(errors.sendAt)}
                        />
                      </div>
                      {touched.sendAt && errors.sendAt ? (
                        <Typography variant="caption" color="error">
                          {errors.sendAt}
                        </Typography>
                      ) : (
                        sendValid &&
                        !isReadOnly && (
                          <Typography className={classes.confirmLine}>
                            Será enviada <strong>{WEEKDAYS[sendMoment.day()]}, {sendMoment.format("DD/MM")}, às {timePart}</strong>{" "}
                            ({relativeTime(sendMoment.toDate())}).
                          </Typography>
                        )
                      )}
                    </FormSection>

                    {!isReadOnly && (
                      <FormSection title="Depois do envio">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={keepTicketOpen}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setKeepTicketOpen(checked);
                                if (!checked) setScheduleQueueId("");
                              }}
                              color="primary"
                            />
                          }
                          label="Abrir atendimento para a resposta"
                        />
                        <Typography className={classes.switchHelp}>
                          Se o contato responder, a conversa já cai no departamento escolhido.
                        </Typography>
                        {keepTicketOpen && (
                          <FormControl variant="outlined" fullWidth>
                            <Autocomplete
                              fullWidth
                              value={queues.find((q) => q.id === scheduleQueueId) || null}
                              options={queues}
                              onChange={(e, queue) => {
                                setScheduleQueueId(queue ? queue.id : "");
                              }}
                              getOptionLabel={(option) => option.name || ""}
                              getOptionSelected={(option, value) => option.id === value.id}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  variant="outlined"
                                  size="small"
                                  label="Departamento para receber resposta"
                                />
                              )}
                            />
                          </FormControl>
                        )}
                      </FormSection>
                    )}
                  </div>

                  <aside className={classes.preview} aria-label="Prévia da mensagem">
                    <Typography className={classes.previewTitle}>Como o contato vai ver</Typography>
                    <div className={classes.bubble}>
                      {attachmentName && (
                        <div className={classes.bubbleFile}>
                          <AttachFile fontSize="small" />
                          {attachmentName}
                        </div>
                      )}
                      <div className={classes.bubbleText}>
                        {preview || "A mensagem aparece aqui enquanto você escreve."}
                      </div>
                      <div className={classes.bubbleTime}>{timePart}</div>
                    </div>
                    <Typography className={classes.previewHint}>
                      As variáveis já aparecem preenchidas com os dados do contato.
                    </Typography>
                  </aside>
                </DialogContent>

                <DialogActions className={dialogClasses.actions}>
                  {scheduleId && (
                    <Button
                      onClick={() => setDeleteConfirmOpen(true)}
                      disabled={isSubmitting}
                      className={`${dialogClasses.actionButton} ${classes.deleteButton}`}
                    >
                      Excluir
                    </Button>
                  )}
                  <Button
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className={`${dialogClasses.actionButton} ${dialogClasses.cancelButton}`}
                  >
                    {isReadOnly ? "Fechar" : i18n.t("scheduleModal.buttons.cancel")}
                  </Button>
                  {!isReadOnly && (
                    <div className={dialogClasses.progressWrapper}>
                      <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        disableElevation
                        disabled={isSubmitting}
                        className={dialogClasses.actionButton}
                      >
                        {needsAttention ? "Reagendar" : scheduleId ? "Salvar" : "Agendar"}
                        {sendValid ? ` para ${describeSendAt(values.sendAt)}` : ""}
                      </Button>
                      {isSubmitting && (
                        <CircularProgress size={24} className={dialogClasses.buttonProgress} />
                      )}
                    </div>
                  )}
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Dialog>
    </div>
  );
};

export default ScheduleModal;
